import { type Fingering } from '../guitar/library.js'

export enum Duration {
  QuarterNote = 'q',
  HalfNote = 'h',
  WholeNote = 'w',
  EightsNote = '8',
  SixteenthNote = '16',
}

export enum NoteFinish {
  Vibrato = 'v',
  StrongVibrato = 'V',
}

export enum InstrumentType {
  Guitar = 'guitar',
  Bass = 'bass',
}

/** https://en.wikipedia.org/wiki/ASCII_tab */
export interface TabStep {
  fingering: Fingering | Fingering[]

  duration?: Duration
  // articulation?: Articulation
  finish?: NoteFinish
}

export interface Tab {
  name?: string
  /** Tablang string */
  lang?: string
  /** Instrument type (guitar or bass) - defaults to guitar for backward compatibility */
  instrument?: InstrumentType
  steps: TabStep[]
}

const guitarStringLabels = ['e', 'B', 'G', 'D', 'A', 'E']
const bassStringLabels = ['G', 'D', 'A', 'E']

export const tabToAscii = (tab: Tab) => {
  /** Map out the tab in a sequence. The sequence is sequence[string][sequenceNo] */
  const isBass = tab.instrument === InstrumentType.Bass
  const stringCount = isBass ? 4 : 6
  const stringLabels = isBass ? bassStringLabels : guitarStringLabels
  const sequence: (number | string)[][] = Array.from({ length: stringCount }, () => [])
  const tabSteps = tab.steps

  for (let stepIndex = 0; stepIndex < tabSteps.length; stepIndex++) {
    const tabStep = tabSteps[stepIndex]
    const stringNums: number[] = Array.from({ length: stringCount }, (_, i) => i)
    const isLastStep = stepIndex === tabSteps.length - 1

    const fingerings = Array.isArray(tabStep.fingering) ? tabStep.fingering : [tabStep.fingering]

    if (fingerings.length > 0) {
      // Handle regular fingering
      /** @todo when one of the is double digit, we need to use '--' for the muted strings, or 7- for the single digit strings */
      const maxFret = Math.max(...fingerings.map(f => f.fret as number))
      const hasDoubleDigit = maxFret >= 10
      const hasArticulation = fingerings.some(f => f.articulation)

      for (const str of stringNums) {
        const fingering = fingerings.find(f => f.str === str + 1)
        if (fingering?.fret !== undefined && fingering.fret !== null) {
          // Add fret number
          const fretStr = fingering.fret.toString()
          for (const char of fretStr) {
            sequence[str].push(char)
          }
          // Add articulation if present
          if (fingering.articulation) {
            sequence[str].push(fingering.articulation)
          } else if (hasArticulation) {
            // No articulation on this fingering, but other strings have articulations
            // Add a padding dash to maintain alignment
            sequence[str].push('-')
          }
          // Pad with dash if needed for alignment with double-digit frets
          if (hasDoubleDigit) {
            const totalLength = fretStr.length + (fingering.articulation ? 1 : 0)
            // Double digit frets are 2 chars, or 3 with articulation
            const targetLength = fingering.articulation ? 3 : 2
            for (let i = totalLength; i < targetLength; i++) {
              sequence[str].push('-')
            }
          }
        } else {
          // No fingering on this string - add dashes to match the width of strings with fingerings
          sequence[str].push('-')
          if (hasDoubleDigit) {
            sequence[str].push('-')
          }
          if (hasArticulation) {
            sequence[str].push('-') // Extra dash for articulation space
          }
        }
      }

      // Add trailing dash after each note step, except for the last step
      if (!isLastStep) {
        for (const str of stringNums) {
          sequence[str].push('-')
        }
      }
    } else {
      // Handle pause steps
      if (stepIndex === 0) {
        // First step is a pause - add two dashes (no previous trailing dash)
        for (const str of stringNums) {
          sequence[str].push('-')
          sequence[str].push('-')
        }
      } else {
        // Subsequent pause steps - add one dash
        // (the trailing dash from previous note steps provides the spacing)
        for (const str of stringNums) {
          sequence[str].push('-')
        }
      }
    }
  }

  // Check if this tab contains any pause steps (empty fingering arrays)
  const hasPauses = tab.steps.some(step => {
    const fingerings = Array.isArray(step.fingering) ? step.fingering : [step.fingering]
    return fingerings.length === 0
  })

  // Use different formats based on whether we have pauses
  if (hasPauses) {
    // For tabs with pauses, use format compatible with ASCII parser: |content|
    return sequence.map((s, idx) => `${stringLabels[idx]} |${s.join('')}|`)
  } else {
    // For tabs without pauses, use original format: |-content-|
    return sequence.map((s, idx) => `${stringLabels[idx]} |-${s.join('')}-|`)
  }
}
